import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorIcon from "@mui/icons-material/Error";
import ReplayIcon from "@mui/icons-material/Replay";
import {
	Box,
	Chip,
	CircularProgress,
	LinearProgress,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { Button, FilePreview, FormCard } from "components";
import {
	AsrsUploadColumnHint,
	IsAsrsUpload,
	ResolveAsrsByLpn,
} from "lib/asrs/uploadLayout";
import {
	AcceptedUploadFileType,
	FlexNetUploadPartSerialColumnsRequired,
	FlexNetUploadSerialOnlyColumnsRequired,
} from "lib/flexnet/uploadTemplate";
import * as hooks from "lib/hooks";
import * as opt from "lib/opt";
import * as result from "lib/result";
import { useDropzone } from "react-dropzone";

export function FileUploadPane(): React.JSX.Element {
	const { loading, f, formActions, stepStatus, setStepStatus, resetStepStatus } =
		hooks.UseFormContext();
	const { lockMode, unlockMode, byLpn } = hooks.UseAddSerialMode();
	const theme = useTheme();
	const partNumberRequired = f.watch("partNumberRequired");
	const uploadContentType = f.watch("uploadContentType");
	const asrsByLpn = ResolveAsrsByLpn({
		byLpnFromUrl: byLpn,
		uploadContentType,
	});
	const asrsUpload = IsAsrsUpload({
		lockMode,
		unlockMode,
		uploadContentType,
	});
	const fileErrorMessage = f.formState.errors.file?.message;
	const hasUploadError = stepStatus.IsErr() || !!fileErrorMessage;
	const uploadErrorText =
		typeof fileErrorMessage === "string"
			? fileErrorMessage
			: stepStatus.IsErr()
				? stepStatus.UnwrapErr().message
				: "Please upload a valid Excel (.xlsx) or CSV (.csv) file";
	const uploadColumnHint = asrsUpload
		? AsrsUploadColumnHint(asrsByLpn)
		: lockMode || unlockMode || partNumberRequired == null
			? AcceptedUploadFileType
			: partNumberRequired
				? FlexNetUploadPartSerialColumnsRequired
				: FlexNetUploadSerialOnlyColumnsRequired;

	const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
		onDrop: formActions.newFile,
		onDropRejected: (rejectedFiles) => {
			const errorMsg =
				rejectedFiles.length > 1
					? "Invalid File. Please upload only one file."
					: "Invalid File. Only Excel (.xlsx) and CSV (.csv) are supported";
			f.setError("file", {
				type: "error",
				message: errorMsg,
			});
			setStepStatus(result.Err(new Error(errorMsg)));
		},
		// Matched by MIME or extension. Windows often reports .csv as
		// application/vnd.ms-excel, so .csv is accepted by extension while
		// .xls (same MIME) is still rejected.
		accept: {
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
				[".xlsx"],
			"text/csv": [".csv"],
		},
		maxFiles: 1,
		multiple: false,
		noClick: true,
		disabled: loading,
	});

	const handleClick = () => {
		if (!loading) {
			inputRef.current?.click();
		}
	};

	const fileInfo = f.getValues("file");
	const hasFile = fileInfo.IsSome();

	if (hasFile && !loading) {
		return (
			<FormCard label="Data Preview" toolTip="Preview of uploaded data">
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					mb={2}
				>
					<Chip
						label={fileInfo
							.Map((fi: File) => fi.name)
							.UnwrapOr("")}
						color="primary"
						variant="outlined"
						sx={{
							borderRadius: 6,
							py: 2,
							px: 1,
							fontWeight: 500,
							fontSize: "0.85rem",
							borderColor: alpha(theme.palette.primary.main, 0.4),
							backgroundColor: alpha(theme.palette.primary.main, 0.05),
						}}
					/>
					<Button
						variant="outlined"
						size="small"
						startIcon={<ReplayIcon />}
						onClick={() => {
							f.setValue("file", opt.None());
							f.setValue("parsed", []);
							f.clearErrors("file");
							resetStepStatus();
						}}
						sx={{
							borderRadius: 20,
							textTransform: "none",
							fontWeight: 500,
						}}
					>
						Re-upload
					</Button>
				</Box>
				<FilePreview showHighlightedCols hideTitle />
			</FormCard>
		);
	}

	const uploadLabel = (lockMode || unlockMode)
		? (byLpn ? "Upload LPN list" : "Upload Ultium serial number list")
		: "Upload Containment File";

	return (
		<FormCard
			label={uploadLabel}
			toolTip="Drag and drop or click to upload your Excel (.xlsx) or CSV (.csv) file"
		>
			<Box
				{...getRootProps()}
				onClick={handleClick}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						handleClick();
					}
				}}
				role="button"
				tabIndex={0}
				aria-label={uploadLabel}
				aria-disabled={loading}
				sx={{
					"display": "flex",
					"alignItems": "center",
					"justifyContent": "center",
					"flexDirection": "column",
					"width": "100%",
					"flex": 1,
					"minHeight": 200,
					"textAlign": "center",
					"cursor": loading ? "wait" : "pointer",
					"borderRadius": 3,
					"backgroundColor": isDragActive
						? alpha(theme.palette.primary.main, 0.05)
						: alpha(theme.palette.background.paper, 0.6),
					"border": `2px dashed ${
						hasUploadError
							? alpha(theme.palette.error.main, 0.6)
							: alpha(theme.palette.primary.main, 0.4)
					}`,
					"transition": "all 0.3s ease",
					"opacity": loading ? 0.7 : 1,
					"&:hover": {
						backgroundColor: alpha(theme.palette.primary.main, 0.05),
						borderColor: alpha(theme.palette.primary.main, 0.6),
					},
					"&:focus": {
						outline: "none",
						boxShadow: `0 0 0 3px ${alpha(
							hasUploadError
								? theme.palette.error.main
								: theme.palette.primary.main,
							0.2
						)}`,
					},
					"position": "relative",
				}}
			>
				{loading && (
					<Box
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: alpha(theme.palette.background.paper, 0.8),
							backdropFilter: "blur(4px)",
							borderRadius: 3,
							zIndex: 10,
						}}
					>
						<CircularProgress
							size={48}
							sx={{ color: theme.palette.primary.main, mb: 2 }}
						/>
						<Typography variant="body2" sx={{ fontWeight: 500 }}>
							Processing file...
						</Typography>
					</Box>
				)}

				<input
					key={fileInfo
						.Map((fi: File) => fi.name)
						.UnwrapOr("no-file")}
					{...getInputProps()}
					ref={inputRef}
					onChange={(e) => {
						if (!loading) {
							opt.FromNullable(e.target.files)
								.FilterMap((files) =>
									opt
										.Some(Array.from(files))
										.Filter((arr) => !arr.IsEmpty())
								)
								.Tap(formActions.newFile);
						}
					}}
					disabled={loading}
				/>

				{hasUploadError ? (
					<ErrorIcon
						sx={{ fontSize: 40, color: theme.palette.error.main, mb: 1.5 }}
					/>
				) : (
					<CloudUploadIcon
						sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }}
					/>
				)}

				{hasUploadError ? (
					<>
						<Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
							Import failed
						</Typography>
						<Typography
							variant="body2"
							color="error"
							sx={{
								px: 2,
								maxWidth: 420,
								mb: 2,
								whiteSpace: "pre-line",
							}}
						>
							{uploadErrorText}
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							size="small"
							startIcon={<CloudUploadIcon />}
							onClick={(e) => {
								e.stopPropagation();
								handleClick();
							}}
							sx={{
								borderRadius: 20,
								textTransform: "none",
								fontWeight: 600,
								mb: 1,
							}}
						>
							Choose another file
						</Button>
						<Typography variant="caption" color="textSecondary">
							{isDragActive
								? "Drop your file here"
								: "or drag & drop a file to try again"}
						</Typography>
					</>
				) : (
					<>
						<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
							{isDragActive
								? "Drop your file here"
								: "Drag & drop your file here"}
						</Typography>
						<Typography variant="body2" color="textSecondary">
							or click to browse files from your device
						</Typography>
						<Typography
							variant="caption"
							color="textSecondary"
							sx={{ mt: 1, opacity: 0.6, lineHeight: 1.5, whiteSpace: "pre-line" }}
						>
							{uploadColumnHint}
						</Typography>
					</>
				)}
			</Box>

			{loading && (
				<Box sx={{ width: "100%", mt: 2 }}>
					<LinearProgress
						sx={{
							"height": 3,
							"borderRadius": 2,
							"& .MuiLinearProgress-bar": {
								borderRadius: 2,
								backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
							},
						}}
					/>
					<Typography
						variant="caption"
						align="center"
						display="block"
						sx={{ mt: 0.5, opacity: 0.7, fontWeight: 500 }}
					>
						Processing file... Please wait.
					</Typography>
				</Box>
			)}
		</FormCard>
	);
}
